import { socketService } from './socket.service';
import { Platform, Vibration } from 'react-native';

class NotificationService {
  private static instance: NotificationService;
  private showClickableNotificationHandler?: (type: 'success' | 'error' | 'info' | 'warning' | 'message', title: string, message: string, action?: { label: string; onPress: () => void; channel?: string; channelName?: string }) => void;
  private navigateToConversationHandler?: (conversationId: string) => void;
  private isCurrentScreenChatHandler?: () => boolean;
  private getCurrentBusinessIdHandler?: () => string | undefined;
  private isInitialized = false;

  private constructor() {
    // Don't initialize socket listeners in constructor
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Set notification handlers and screen checker
   */
  setNotificationHandler(
    showClickableNotification: (type: 'success' | 'error' | 'info' | 'warning' | 'message', title: string, message: string, action?: { label: string; onPress: () => void; channel?: string; channelName?: string }) => void,
    navigateToConversation: (conversationId: string) => void,
    isCurrentScreenChat: () => boolean,
    getCurrentBusinessId?: () => string | undefined
  ) {
    this.showClickableNotificationHandler = showClickableNotification;
    this.navigateToConversationHandler = navigateToConversation;
    this.isCurrentScreenChatHandler = isCurrentScreenChat;
    this.getCurrentBusinessIdHandler = getCurrentBusinessId;
    
    // Initialize socket listeners only after handlers are set
    this.initializeSocketListeners();
    this.isInitialized = true;
  }

  /**
   * Initialize socket listeners for new message notifications
   */
  initializeSocketListeners(): void {
    if (!this.showClickableNotificationHandler || !this.navigateToConversationHandler || !this.isCurrentScreenChatHandler) {
      console.log('❌ [NotificationService] Handlers not ready');
      return;
    }

    console.log('✅ [NotificationService] Initializing socket listeners');
    console.log('🔧 [NotificationService] Handler status:', {
      hasShowNotificationHandler: !!this.showClickableNotificationHandler,
      hasNavigationHandler: !!this.navigateToConversationHandler,
      hasScreenCheckHandler: !!this.isCurrentScreenChatHandler
    });

    // Listen for new message events from socket service
    import('./socket.service').then(({ socketService }) => {
      console.log('✅ [NotificationService] Socket service imported');
      
      try {
        // Check if the socket service has the required method
        if (typeof socketService.onNewMessage === 'function') {
          console.log('🔧 [NotificationService] Setting up onNewMessage listener');
          socketService.onNewMessage((data) => {
            console.log('📨 [NotificationService] New message received:', data);
            console.log('🔧 [NotificationService] Message data details:', {
              hasContactName: !!data.contactName,
              hasPhoneNumber: !!data.phoneNumber,
              hasMessage: !!data.message,
              hasChannel: !!data.channel,
              hasConversationId: !!data.conversationId,
              hasBusinessId: !!data.businessId
            });
            
            // Filter by business ID if available
            const currentBusinessId = this.getCurrentBusinessIdHandler?.();
            if (data.businessId && currentBusinessId && data.businessId !== currentBusinessId) {
              console.log('🔍 [NotificationService] Message from different business, ignoring:', {
                messageBusinessId: data.businessId,
                currentBusinessId: currentBusinessId
              });
              return;
            }
            
            // Check if user is currently on the chat screen
            const isOnChatScreen = this.isCurrentScreenChatHandler?.();
            console.log('📱 [NotificationService] Is on chat screen:', isOnChatScreen);
            
            // Only show notification if NOT on chat screen
            if (!isOnChatScreen && this.showClickableNotificationHandler && this.navigateToConversationHandler) {
              console.log('🔔 [NotificationService] Showing notification');
              
              // Extract message details
              const contactName = data.contactName || data.phoneNumber || 'Unknown Contact';
              const messageContent = this.getMessagePreview(data.message);
              const channelName = this.getChannelDisplayName(data.channel);
              const channelIcon = this.getChannelIcon(data.channel);
              
              // Add haptic feedback and sound
              this.triggerNotificationFeedback();
              
              this.showClickableNotificationHandler(
                'message', // Use a new 'message' type for better styling
                contactName, // Clean contact name without emoji
                messageContent, // Just the message content
                {
                  label: 'View',
                  onPress: () => {
                    if (this.navigateToConversationHandler) {
                      // Navigate to inbox (chat screen) instead of specific conversation
                      // since we have unified inbox now
                      this.navigateToConversationHandler('inbox');
                    }
                  },
                  channel: data.channel, // Pass channel info for icon
                  channelName: channelName
                }
              );
            } else {
              console.log('❌ [NotificationService] Not showing notification - on chat screen or handlers missing');
            }
          });
        } else {
          console.warn('⚠️ [NotificationService] Socket service does not have onNewMessage method');
        }
      } catch (error) {
        console.warn('⚠️ [NotificationService] Failed to initialize socket listeners:', error);
      }
    }).catch((error) => {
      console.warn('⚠️ [NotificationService] Failed to import socket service:', error);
    });
  }

  /**
   * Get message preview text based on message type
   */
  private getMessagePreview(message: any): string {
    if (!message) return 'New message';
    
    // Handle different message types
    switch (message.type) {
      case 'text':
        return message.content || message.text?.body || 'Text message';
      case 'image':
        return '📷 Image';
      case 'video':
        return '🎥 Video';
      case 'audio':
        return '🎵 Audio message';
      case 'document':
        return '📄 Document';
      case 'location':
        return '📍 Location';
      case 'contact':
        return '👤 Contact';
      case 'sticker':
        return '😊 Sticker';
      default:
        return message.content || message.body || 'New message';
    }
  }

  /**
   * Get channel display name
   */
  private getChannelDisplayName(channel: string): string {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
        return 'WhatsApp';
      case 'telegram':
        return 'Telegram';
      case 'facebook':
        return 'Facebook';
      case 'instagram':
        return 'Instagram';
      case 'twitter':
      case 'x':
        return 'X';
      case 'email':
        return 'Email';
      default:
        return 'Message';
    }
  }

  /**
   * Get channel icon emoji
   */
  private getChannelIcon(channel: string): string {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
        return '💬';
      case 'telegram':
        return '✈️';
      case 'facebook':
        return '📘';
      case 'instagram':
        return '📷';
      case 'twitter':
      case 'x':
        return '🐦';
      case 'email':
        return '📧';
      default:
        return '💬';
    }
  }

  /**
   * Trigger haptic feedback and sound for notification
   */
  private triggerNotificationFeedback(): void {
    try {
      // Vibrate on mobile platforms
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        // Short vibration pattern for message notifications
        Vibration.vibrate([0, 200, 100, 200]);
      }
      
      // TODO: Add sound notification when expo-av is available
      console.log('🔊 [NotificationService] Notification feedback triggered');
    } catch (error) {
      console.warn('⚠️ [NotificationService] Failed to trigger notification feedback:', error);
    }
  }

  public showNewMessageNotification(conversationId: string, contactName: string, message: string, channel: string = 'whatsapp') {
    if (this.showClickableNotificationHandler && this.navigateToConversationHandler) {
      const channelIcon = this.getChannelIcon(channel);
      const channelName = this.getChannelDisplayName(channel);
      
      // Add haptic feedback
      this.triggerNotificationFeedback();
      
      this.showClickableNotificationHandler(
        'message', // Use new message type
        contactName, // Clean contact name
        message, // Just the message
        {
          label: 'View',
          onPress: () => {
            this.navigateToConversationHandler!('inbox');
          },
          channel: channel, // Pass channel info
          channelName: channelName
        }
      );
    }
  }

  /**
   * Test notification functionality (for debugging)
   */
  public testNotification(): void {
    console.log('🧪 [NotificationService] Testing notification functionality');
    
    const isOnChatScreen = this.isCurrentScreenChatHandler?.();
    console.log('🧪 [NotificationService] Current screen is chat:', isOnChatScreen);
    
    if (this.showClickableNotificationHandler && this.navigateToConversationHandler) {
      console.log('🧪 [NotificationService] Showing test notification');
      
      // Trigger haptic feedback
      this.triggerNotificationFeedback();
      
      this.showClickableNotificationHandler(
        'message', // Use message type
        'Test Contact', // Clean name
        'This is a test notification!', // Clean message
        {
          label: 'View',
          onPress: () => {
            console.log('🧪 [NotificationService] Test notification clicked');
            this.navigateToConversationHandler!('inbox');
          },
          channel: 'whatsapp', // Test with WhatsApp
          channelName: 'WhatsApp'
        }
      );
    } else {
      console.log('❌ [NotificationService] Test failed - handlers not available');
    }
  }

  /**
   * Simulate a new message event (for debugging)
   */
  public simulateNewMessage(): void {
    console.log('🧪 [NotificationService] Simulating new message event');
    
    const mockData = {
      conversationId: 'test-conv-123',
      contactName: 'Test Contact',
      phoneNumber: '+1234567890',
      channel: 'whatsapp',
      message: {
        type: 'text',
        content: 'This is a simulated message!'
      }
    };
    
    // Simulate the socket event
    if (this.showClickableNotificationHandler && this.navigateToConversationHandler) {
      console.log('🧪 [NotificationService] Processing simulated message:', mockData);
      
      const isOnChatScreen = this.isCurrentScreenChatHandler?.();
      console.log('🧪 [NotificationService] Is on chat screen:', isOnChatScreen);
      
      if (!isOnChatScreen) {
        this.showClickableNotificationHandler(
          'message',
          mockData.contactName,
          mockData.message.content,
          {
            label: 'View',
            onPress: () => this.navigateToConversationHandler!('inbox'),
            channel: mockData.channel,
            channelName: 'WhatsApp'
          }
        );
      } else {
        console.log('🧪 [NotificationService] Not showing notification - on chat screen');
      }
    }
  }

  /**
   * Get service status for debugging
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      hasHandlers: !!(this.showClickableNotificationHandler && this.navigateToConversationHandler && this.isCurrentScreenChatHandler)
    };
  }
}

export const notificationService = NotificationService.getInstance();

// Make notification service available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testNotification = () => {
    console.log('🧪 [Global] Testing notification from window');
    notificationService.testNotification();
  };
  (window as any).simulateNewMessage = () => {
    console.log('🧪 [Global] Simulating new message from window');
    notificationService.simulateNewMessage();
  };
  (window as any).notificationService = notificationService;
  (window as any).checkSocketConnection = () => {
    console.log('🔧 [Global] Checking socket connection');
    const { socketService } = require('./socket.service');
    console.log('Socket connected:', socketService.isConnected());
    return socketService.isConnected();
  };
  (window as any).testSocketEvent = (data: any) => {
    console.log('🧪 [Global] Testing socket event:', data);
    const { socketService } = require('./socket.service');
    // Simulate the socket event
    socketService.emit('new-message', data);
  };
}
